import { PublicWelcome } from "./PublicWelcome";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
  useRef,
  type ReactNode,
} from "react";
import type { OperatorIdentity } from "@proof/operator";
import {
  operatorContextSchema,
  type OperatorContext,
} from "@proof/authorization";
import {
  OperatorContextShell,
  OperatorTenantEntry,
} from "./OperatorImpersonation";
import { InstanceSetup } from "./InstanceSetup";
import { ApplicationAccounts } from "./ApplicationAccounts";
import {
  OperatorConfiguration,
  type AppliedConfiguration,
} from "./OperatorConfiguration";
import {
  OperatorLogin,
  OperatorPassword,
  OperatorHome,
  operatorNavigate,
} from "./OperatorAccess";
import {
  instanceRequest,
  InstanceRequestError,
  type InstanceStatus,
} from "./instance-api";
import { safeOperatorReturn } from "./setup-model";
import { Button } from "./components";
import { PublicDocumentation } from "./PublicDocumentation";
const Demo = lazy(() =>
  import("./App").then((module) => ({ default: module.App })),
);
const Tenant = lazy(() =>
  import("./TenantWorkspace").then((module) => ({
    default: module.TenantWorkspace,
  })),
);
const Impersonation = lazy(() =>
  import("./Impersonation").then((module) => ({
    default: module.Impersonation,
  })),
);
const NativePreview = lazy(() =>
  import("./NativePreview").then((module) => ({
    default: module.NativePreview,
  })),
);
type Gate =
  | { kind: "checking" }
  | { kind: "unavailable" }
  | {
      kind: "loaded";
      status: InstanceStatus;
      identity?: OperatorIdentity | null;
      impersonation?: OperatorContext | null;
    };
export function ApplicationRoot() {
  const [applied, setApplied] = useState<AppliedConfiguration>();
  const native = useRef(false);
  const hadOperator = useRef(false);
  const [route, setRoute] = useState(
      () => location.pathname + location.search + location.hash,
    ),
    [revision, setRevision] = useState(0),
    [gate, setGate] = useState<Gate>({ kind: "checking" });
  const refresh = useCallback(() => {
    setGate({ kind: "checking" });
    setRevision((old) => old + 1);
  }, []);
  useEffect(() => {
    const navigate = () => {
      setRoute(location.pathname + location.search + location.hash);
      if (native.current) setGate({ kind: "checking" });
    };
    window.addEventListener("popstate", navigate);
    window.addEventListener("hashchange", navigate);
    return () => {
      window.removeEventListener("popstate", navigate);
      window.removeEventListener("hashchange", navigate);
    };
  }, []);
  useEffect(() => {
    if (
      /^\/(help|docs|welcome|signup|request-access)(\/|$)/.test(
        location.pathname,
      )
    )
      return;
    let current = true,
      loading = false;
    const controller = new AbortController();
    const load = async () => {
      if (loading) return;
      loading = true;
      try {
        const status = await instanceRequest<InstanceStatus>(
          "/api/instance/status",
          undefined,
          undefined,
          controller.signal,
        );
        if (!current) return;
        if (
          status.version !== 1 ||
          !["environment", "instance", "application"].includes(status.mode)
        )
          throw new Error();
        native.current = status.mode === "instance";
        let identity: OperatorIdentity | null | undefined;
        let impersonation: OperatorContext | null = null;
        let operatorSessionPresent = false;
        if (
          status.mode === "instance" &&
          status.phase === "ready" &&
          status.configuration?.publicOrigin === location.origin
        ) {
          const session = await instanceRequest<{
            identity: OperatorIdentity | null;
            operatorSessionPresent?: boolean;
          }>("/api/operator/session", undefined, undefined, controller.signal);
          identity = session.identity;
          operatorSessionPresent = session.operatorSessionPresent === true;
        }
        if (identity && !identity.passwordChangeRequired) {
          const view = await instanceRequest<{
            impersonation: OperatorContext | null;
          }>(
            "/api/operator/impersonation",
            undefined,
            undefined,
            controller.signal,
          );
          impersonation = view.impersonation
            ? operatorContextSchema.parse(view.impersonation)
            : null;
          if (
            impersonation &&
            impersonation.actorOperatorId !== identity.operatorId
          )
            throw new Error("Operator context changed");
        }
        if (!current) return;
        const lostOperator = hadOperator.current && !identity;
        hadOperator.current = !!identity;
        setGate({ kind: "loaded", status, identity, impersonation });
        const operator =
            location.pathname.startsWith("/operator") ||
            location.pathname === "/admin/renders",
          destination = safeOperatorReturn(
            new URLSearchParams(location.search).get("return") ||
              (location.pathname === "/admin/renders" ||
              location.pathname === "/operator/configuration" ||
              location.pathname === "/operator/impersonation"
                ? location.pathname
                : null),
          );
        if (
          status.mode === "instance" &&
          status.phase === "setup-required" &&
          location.pathname !== "/setup"
        )
          operatorNavigate("/setup", true);
        else if (
          status.mode === "instance" &&
          status.phase === "ready" &&
          status.configuration?.publicOrigin === location.origin
        ) {
          if (
            !identity &&
            (lostOperator || operatorSessionPresent) &&
            location.pathname !== "/operator/login"
          )
            operatorNavigate(
              `/operator/login?return=${encodeURIComponent(destination)}`,
              true,
            );
          else if (
            impersonation &&
            (operator || location.pathname === "/setup")
          )
            operatorNavigate("/", true);
          else if (
            identity?.passwordChangeRequired &&
            location.pathname !== "/operator/password"
          )
            operatorNavigate(
              `/operator/password?return=${encodeURIComponent(destination)}`,
              true,
            );
          else if (
            operator &&
            !identity &&
            location.pathname !== "/operator/login"
          )
            operatorNavigate(
              `/operator/login?return=${encodeURIComponent(destination)}`,
              true,
            );
          else if (
            identity &&
            !impersonation &&
            (location.pathname === "/operator/login" ||
              location.pathname === "/setup" ||
              !operator)
          )
            operatorNavigate(destination, true);
          else if (location.pathname === "/setup")
            operatorNavigate("/operator/login", true);
        }
      } catch (cause) {
        if (current)
          setGate(
            cause instanceof InstanceRequestError &&
              cause.code === "CONFIGURATION_RESTART_REQUIRED"
              ? {
                  kind: "loaded",
                  status: {
                    version: 1,
                    mode: "instance",
                    phase: "restart-required",
                    ready: false,
                    error: cause.code,
                  },
                }
              : { kind: "unavailable" },
          );
      } finally {
        loading = false;
      }
    };
    void load();
    const timer = setInterval(() => {
      if (native.current) void load();
    }, 5000);
    const focus = () => {
      if (native.current) void load();
    };
    window.addEventListener("focus", focus);
    return () => {
      current = false;
      controller.abort();
      clearInterval(timer);
      window.removeEventListener("focus", focus);
    };
  }, [route, revision]);
  if (/^\/(welcome|signup|request-access)(\/|$)/.test(location.pathname))
    return <PublicWelcome />;
  if (/^\/(help|docs)(\/|$)/.test(location.pathname))
    return <PublicDocumentation />;
  if (applied)
    return (
      <Neutral title="Restart required">
        <p>
          Configuration revision <code>{applied.revision}</code> was accepted.
          Restart every API before reopening traffic.
        </p>
        <p>Keep the private recovery file until the new revision is ready.</p>
        <a href={`${applied.publicOrigin}/operator`}>
          Open operator access after restart
        </a>
      </Neutral>
    );
  if (gate.kind === "checking") return <Neutral title="Checking access…" />;
  if (gate.kind === "unavailable")
    return (
      <Neutral title="Runtime unavailable" onRetry={refresh}>
        <p>
          The API could not be checked. No protected content has been opened.
        </p>
      </Neutral>
    );
  const { status, identity, impersonation } = gate;
  if (status.mode === "application") return <ApplicationAccounts />;
  if (status.mode === "environment") {
    if (
      location.pathname.startsWith("/operator") ||
      location.pathname === "/admin/renders" ||
      location.pathname === "/setup" ||
      location.pathname === "/proof-preview"
    )
      return (
        <Neutral title="Instance setup mode is not enabled">
          <p>The existing demo is available at the home page.</p>
          <Button onClick={() => operatorNavigate("/")}>Open demo</Button>
        </Neutral>
      );
    return (
      <Suspense fallback={<Neutral title="Opening workspace…" />}>
        {location.pathname === "/impersonation" ? (
          <Impersonation key={location.hash} />
        ) : (
          <Demo />
        )}
      </Suspense>
    );
  }
  if (status.phase === "setup-required")
    return (
      <InstanceSetup
        available={status.setup?.available === true}
        platform={status.setup?.platform || "posix"}
        onComplete={refresh}
      />
    );
  if (status.phase !== "ready")
    return (
      <Neutral
        title={
          status.phase === "activating"
            ? "Activating instance…"
            : status.phase === "restart-required"
              ? "Restart required"
              : "Runtime unavailable"
        }
        onRetry={refresh}
      >
        <p>
          {status.phase === "activating"
            ? "The saved providers are being initialized."
            : status.phase === "restart-required"
              ? "Configuration changed. Restart every API with the published revision before reopening traffic."
              : "Repair the reported provider and restart the API, then retry."}
        </p>
        {status.error && <code>{status.error}</code>}
      </Neutral>
    );
  if (status.configuration?.publicOrigin !== location.origin)
    return (
      <Neutral title="Use the configured public address">
        <p>Setup and operator actions use the canonical origin.</p>
        <a href={`${status.configuration?.publicOrigin}/operator`}>
          Open operator access
        </a>
      </Neutral>
    );
  if (identity && impersonation)
    return (
      <OperatorContextShell
        key={impersonation.id}
        context={impersonation}
        onChanged={refresh}
      >
        {(stop) => (
          <Suspense
            fallback={<Neutral title="Checking tenant support access…" />}
          >
            {location.pathname === "/proof-preview" ? (
              <NativePreview key={`${impersonation.id}:${location.hash}`} />
            ) : (
              <Tenant
                key={impersonation.id}
                operatorContextId={impersonation.id}
                onOperatorAccessLost={refresh}
                onOperatorExit={stop}
              />
            )}
          </Suspense>
        )}
      </OperatorContextShell>
    );
  if (!identity && location.pathname === "/proof-preview")
    return (
      <Suspense fallback={<Neutral title="Opening proof preview…" />}>
        <NativePreview key={location.hash} />
      </Suspense>
    );
  if (!identity)
    return location.pathname === "/operator/login" ? (
      <OperatorLogin onChanged={refresh} />
    ) : (
      <Suspense fallback={<Neutral title="Checking workspace access…" />}>
        <Tenant
          developmentIdentity={
            status.configuration?.providers.identity === "development"
          }
        />
      </Suspense>
    );
  if (
    identity.passwordChangeRequired ||
    location.pathname === "/operator/password"
  )
    return (
      <OperatorPassword
        forced={identity.passwordChangeRequired}
        onChanged={refresh}
      />
    );
  if (location.pathname === "/operator/configuration")
    return <OperatorConfiguration onApplied={setApplied} />;
  if (location.pathname === "/operator/impersonation")
    return <OperatorTenantEntry identity={identity} onChanged={refresh} />;
  return (
    <OperatorHome
      identity={identity}
      renders={location.pathname === "/admin/renders"}
      onChanged={refresh}
    />
  );
}
function Neutral({
  title,
  children,
  onRetry,
}: {
  title: string;
  children?: ReactNode;
  onRetry?: () => void;
}) {
  return (
    <main className="instance-shell instance-access">
      <header className="instance-brand">Proof Studio</header>
      <section className="instance-panel">
        <h1 role={title.includes("…") ? "status" : undefined}>{title}</h1>
        {children}
        {onRetry && <Button onClick={onRetry}>Retry access check</Button>}
      </section>
    </main>
  );
}
